import { FC, useCallback, useEffect, useState } from 'react';

import { useFleetFiles } from 'hooks';
import * as FleetFile from 'types/fleet-files';

const OUTCOME_LABELS: Record<FleetFile.SizeTypeSightingOutcome, string> = {
  UNKNOWN_CODE: 'Unknown code',
  UNMAPPED: 'No suggestion',
  INACTIVE: 'Retired suggestion',
  CLASS_CONFLICT: 'Class conflict',
  MAPPED: 'Suggested'
};

const UNRESOLVED: FleetFile.SizeTypeSightingOutcome[] = ['UNKNOWN_CODE', 'UNMAPPED', 'INACTIVE', 'CLASS_CONFLICT'];

interface Props {
  /* opens the create dialog for a code with no crosswalk row */
  onAdd: (code: number) => void;
  /* opens the edit dialog for a code that has a row */
  onOpen: (code: number) => void;
  /* bump to reload after a save elsewhere on the page */
  refreshKey?: number;
}

/*
 * Provider size-type codes the gate lookup has actually met and could not suggest a size type for,
 * most frequent first. This is how new provider codes surface: the lookup API sends only a number,
 * so the last unit number is shown to quote to the provider.
 */
export const UnresolvedSizeTypes: FC<Props> = ({ onAdd, onOpen, refreshKey }) => {
  const { getSizeTypeSightings } = useFleetFiles();
  const [sort, setSort] = useState<'count' | 'lastSeen'>('count');
  const [rows, setRows] = useState<FleetFile.SizeTypeSighting[]>([]);
  const [error, setError] = useState('');
  /*
   * Closed by default: open, it takes half the height the crosswalk grid needs (found in the local
   * end-to-end run). The count in the header still says when there is work waiting.
   */
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await getSizeTypeSightings(UNRESOLVED, sort));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unresolved codes could not be loaded');
    }
  }, [getSizeTypeSightings, sort]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className="card-primary d-flex flex-column" style={{ marginTop: 0 }} data-testid="unresolved-size-types">
      <div className="card-header d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn-close d-flex align-items-center g-2"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <span className="material-icons">{open ? 'expand_less' : 'expand_more'}</span>
          <h4 className="mb-0">CODES THE GATE MET WITHOUT A SUGGESTION ({rows.length})</h4>
        </button>
        <label className="d-flex align-items-center g-2 mb-0">
          Sort by
          <select value={sort} onChange={({ target }) => setSort(target.value as 'count' | 'lastSeen')}>
            <option value="count">Times seen</option>
            <option value="lastSeen">Last seen</option>
          </select>
        </label>
      </div>
      {/* a load failure shows even when closed: an empty count must not hide a broken list */}
      <div className="card-body" hidden={!open && !error}>
        {!!error && (
          <p className="text-danger" role="alert">
            {error}
          </p>
        )}
        {!rows.length && !error ? (
          <p className="text-dark mb-0">None yet. Codes appear here after the gate looks up a unit carrying them.</p>
        ) : (
          <table className="w-100">
            <thead>
              <tr>
                <th>CODE</th>
                <th>WHY</th>
                <th>SEEN</th>
                <th>LAST SEEN</th>
                <th>LAST UNIT</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.code} data-testid={`sighting-${s.code}`}>
                  <td>{s.code}</td>
                  <td>{OUTCOME_LABELS[s.lastOutcome]}</td>
                  <td>{s.seenCount}</td>
                  <td>{new Date(s.lastSeenAt).toLocaleString()}</td>
                  <td>{s.lastUnitNumber ?? ''}</td>
                  <td>
                    {s.hasMapping ? (
                      <button type="button" className="btn-outline" onClick={() => onOpen(s.code)}>
                        Open
                      </button>
                    ) : (
                      <button type="button" className="btn-primary" onClick={() => onAdd(s.code)}>
                        Add
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
